import React, { useEffect, useRef, useState } from 'react';
import { Package, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * LiveRatesBadge — high-trust "live shipping" reassurance pill that sits
 * above the carrier list on the Customer-Info / Review-Order shipping
 * step.
 *
 * Animates a count-up (0 → N carriers) over ~750ms whenever the rate
 * list grows, simulating a live stream as carriers respond. The actual
 * /api/shipping/rates endpoint is a single round-trip behind the scenes
 * (Freightcom polls server-side until done==true), but the count-up
 * preserves the perceived-quality lift of streaming.
 *
 * Three modes via props:
 *   - hidden:   no rates yet (let parent show its loading state)
 *   - live:     real Freightcom rates (mockMode=false) → green badge
 *   - preview:  mock rates (mockMode=true)             → amber badge
 */
const COUNT_DURATION_MS = 750;
const COUNT_FPS = 24;

const useCountUp = (target) => {
  const [n, setN] = useState(0);
  const prevTarget = useRef(0);

  useEffect(() => {
    if (!Number.isFinite(target) || target <= 0) {
      setN(0);
      prevTarget.current = 0;
      return;
    }
    if (target === prevTarget.current) return;

    const start = prevTarget.current;
    const delta = target - start;
    const steps = Math.max(
      1,
      Math.round((COUNT_DURATION_MS / 1000) * COUNT_FPS),
    );
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      // easeOut cubic for a satisfying "settle" at the end
      const t = Math.min(1, i / steps);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(start + delta * eased);
      setN(v);
      if (i >= steps) {
        window.clearInterval(id);
        setN(target);
        prevTarget.current = target;
      }
    }, 1000 / COUNT_FPS);
    return () => window.clearInterval(id);
  }, [target]);

  return n;
};

export const LiveRatesBadge = ({ count = 0, mockMode = false }) => {
  const animated = useCountUp(count);
  if (count <= 0) return null;

  const live = !mockMode;
  return (
    <AnimatePresence>
      <motion.div
        key={`live-rates-${live ? 'live' : 'mock'}`}
        initial={{ opacity: 0, y: -6, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        data-testid="live-rates-badge"
        data-live-rates-count={count}
        data-live-rates-mode={live ? 'live' : 'preview'}
        className={`flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg border-2 shadow-sm ${
          live
            ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-[#6FBF73]'
            : 'bg-amber-50 border-amber-300'
        }`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
              live ? 'bg-[#4A7C59] text-white' : 'bg-amber-500 text-white'
            }`}
            aria-hidden="true"
          >
            <Package className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 leading-tight">
              {live ? (
                <>
                  Live shipping from{' '}
                  <motion.span
                    key={count}
                    initial={{ scale: 1.2, color: '#16A34A' }}
                    animate={{ scale: 1, color: '#0F172A' }}
                    transition={{ duration: 0.35 }}
                    className="inline-block tabular-nums"
                    data-testid="live-rates-count"
                  >
                    {animated}
                  </motion.span>{' '}
                  carrier{animated === 1 ? '' : 's'}
                </>
              ) : (
                <>Preview pricing — {count} estimate{count === 1 ? '' : 's'}</>
              )}
            </p>
            <p className="text-[11px] text-gray-600 leading-tight flex items-center gap-1 mt-0.5">
              <ShieldCheck className="w-3 h-3 text-emerald-600 shrink-0" />
              {live
                ? 'Real-time quotes via Freightcom'
                : 'Real rates appear once API key is configured'}
            </p>
          </div>
        </div>
        {/* Powered-by chip — small, intentionally unobtrusive */}
        <span
          className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-gray-500 shrink-0"
          aria-label="Powered by Freightcom"
        >
          <span className="hidden md:inline">Powered by</span>
          <span className="text-[#0EA5E9] font-extrabold">Freightcom</span>
        </span>
      </motion.div>
    </AnimatePresence>
  );
};

export default LiveRatesBadge;
